import { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { llcApi, checkApi, checkLayoutApi } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { FileText, MoreVertical, Trash2, XCircle, CheckCircle, Printer, Eye, RotateCcw, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { CheckPrintView } from '../components/CheckPrintView';

export const CheckRegisterPage = () => {
  const [llcs, setLlcs] = useState([]);
  const [selectedLLCId, setSelectedLLCId] = useState('all');
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidTargetCheck, setVoidTargetCheck] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [reprintCheck, setReprintCheck] = useState(null);
  const [checkLayout, setCheckLayout] = useState(null);

  const printRef = useRef(null);
  const reprintRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Check-${selectedCheck?.check_number || 'view'}`,
  });

  useEffect(() => {
    fetchLLCs();
    checkLayoutApi.get().then(res => setCheckLayout(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchChecks();
  }, [selectedLLCId]);

  const fetchLLCs = async () => {
    try {
      const response = await llcApi.list();
      setLlcs(response.data);
    } catch (error) {
      console.error('Failed to fetch LLCs:', error);
    }
  };

  const fetchChecks = async () => {
    setLoading(true);
    try {
      const response = await checkApi.list(selectedLLCId !== 'all' ? selectedLLCId : null);
      setChecks(response.data);
    } catch (error) {
      toast.error('Failed to fetch checks');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (checkId, newStatus, voidReasonText = null) => {
    try {
      await checkApi.updateStatus(checkId, newStatus, voidReasonText);
      toast.success(`Check marked as ${newStatus}`);
      fetchChecks();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openVoidDialog = (check) => {
    setVoidTargetCheck(check);
    setVoidReason('');
    setVoidDialogOpen(true);
  };

  const confirmVoid = async () => {
    if (!voidTargetCheck) return;
    await handleStatusChange(voidTargetCheck.id, 'voided', voidReason);
    setVoidDialogOpen(false);
    setVoidTargetCheck(null);
    setVoidReason('');
  };

  const handleReprintDirect = useReactToPrint({
    contentRef: reprintRef,
    documentTitle: `Reprint-Check-${reprintCheck?.check_number || 'view'}`,
  });

  const startReprint = (check) => {
    setReprintCheck(check);
    setTimeout(() => handleReprintDirect(), 200);
  };

  const handleDelete = async (checkId) => {
    try {
      await checkApi.delete(checkId);
      toast.success('Check deleted');
      fetchChecks();
    } catch (error) {
      toast.error('Failed to delete check');
    }
  };

  const handleViewCheck = (check) => {
    setSelectedCheck(check);
    setViewDialogOpen(true);
  };

  const handlePrintFromView = async () => {
    handlePrint();
    if (selectedCheck && selectedCheck.status === 'pending') {
      try {
        await checkApi.updateStatus(selectedCheck.id, 'printed');
        setSelectedCheck(prev => ({ ...prev, status: 'printed' }));
        fetchChecks();
      } catch (error) {
        console.error('Failed to update status:', error);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getLLCName = (llcId) => {
    const llc = llcs.find(l => l.id === llcId);
    return llc?.company_name || 'Unknown';
  };

  const getLLC = (llcId) => {
    return llcs.find(l => l.id === llcId);
  };

  // Calculate running balance (for display purposes)
  const checksWithBalance = [...checks].reverse().reduce((acc, check, index) => {
    const prevBalance = index > 0 ? acc[index - 1].balance : 0;
    const balance = check.status === 'voided' ? prevBalance : prevBalance - check.amount;
    return [...acc, { ...check, balance }];
  }, []).reverse();

  return (
    <div className="space-y-3 fade-in" data-testid="check-register-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Check Register</h1>
          <p className="text-slate-500 mt-1">View and manage all issued checks</p>
        </div>
        <div className="w-64">
          <Select value={selectedLLCId} onValueChange={setSelectedLLCId}>
            <SelectTrigger data-testid="register-llc-filter">
              <SelectValue placeholder="Filter by LLC" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All LLCs</SelectItem>
              {llcs.map(llc => (
                <SelectItem key={llc.id} value={llc.id}>
                  {llc.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="rounded-t-lg panel-header-themed" >
          <CardTitle className="flex items-center gap-2" style={{ color: '#ffffff' }}>
            <FileText className="w-5 h-5" />
            Check History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading checks...</div>
          ) : checks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p>No checks found</p>
              <p className="text-sm mt-1">Create your first check to see it here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Check #</th>
                    <th>Date</th>
                    {selectedLLCId === 'all' && <th>LLC</th>}
                    <th>Payee</th>
                    <th>Memo</th>
                    <th className="text-right">Amount</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {checksWithBalance.map((check) => (
                    <tr 
                      key={check.id} 
                      className={`cursor-pointer hover:bg-slate-100 ${check.status === 'voided' ? 'opacity-50' : ''}`}
                      onClick={() => handleViewCheck(check)}
                      data-testid={`check-row-${check.id}`}
                    >
                      <td className="font-mono font-semibold">{check.check_number}</td>
                      <td>{new Date(check.date).toLocaleDateString()}</td>
                      {selectedLLCId === 'all' && <td>{getLLCName(check.llc_id)}</td>}
                      <td className="max-w-[200px] truncate">{check.payee}</td>
                      <td className="max-w-[150px] truncate text-slate-500">{check.memo || '-'}</td>
                      <td className={`text-right font-mono ${check.status === 'voided' ? 'line-through' : 'amount-negative'}`}>
                        {formatCurrency(check.amount)}
                      </td>
                      <td>
                        <span className={`status-badge status-${check.status}`}>
                          {check.status}
                        </span>
                      </td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`check-actions-${check.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewCheck(check)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Check
                            </DropdownMenuItem>
                            {check.status === 'printed' && (
                              <DropdownMenuItem onClick={() => startReprint(check)}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Reprint Check
                              </DropdownMenuItem>
                            )}
                            {check.status === 'pending' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(check.id, 'printed')}>
                                <Printer className="w-4 h-4 mr-2" />
                                Mark as Printed
                              </DropdownMenuItem>
                            )}
                            {check.status === 'printed' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(check.id, 'pending')}>
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Mark as Unprinted
                              </DropdownMenuItem>
                            )}
                            {check.status !== 'voided' && (
                              <DropdownMenuItem onClick={() => openVoidDialog(check)} className="text-red-600">
                                <XCircle className="w-4 h-4 mr-2" />
                                Void Check
                              </DropdownMenuItem>
                            )}
                            {check.status === 'voided' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(check.id, 'pending')}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Restore Check
                              </DropdownMenuItem>
                            )}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Check?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete check #{check.check_number}. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(check.id)} className="bg-red-600 hover:bg-red-700">
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Check View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Check #{selectedCheck?.check_number}</span>
              <div className="flex gap-2">
                <Button onClick={handlePrintFromView} data-testid="view-print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedCheck && getLLC(selectedCheck.llc_id) && (
            <div className="mt-4">
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="transform scale-[0.55] origin-top-left" style={{ width: '182%' }}>
                  <CheckPrintView
                    ref={printRef}
                    llc={getLLC(selectedCheck.llc_id)}
                    check={selectedCheck}
                    isPreview={false}
                    layout={checkLayout}
                  />
                </div>
              </div>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500 block">Status</span>
                    <span className={`status-badge status-${selectedCheck.status}`}>
                      {selectedCheck.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Created</span>
                    <span className="text-slate-900">{new Date(selectedCheck.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Amount</span>
                    <span className="text-slate-900 font-mono font-semibold">{formatCurrency(selectedCheck.amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">LLC</span>
                    <span className="text-slate-900">{getLLCName(selectedCheck.llc_id)}</span>
                  </div>
                </div>
                {selectedCheck.status === 'voided' && selectedCheck.void_reason && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="void-reason-display">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Void Reason</span>
                        <p className="text-sm text-red-800 mt-0.5">{selectedCheck.void_reason}</p>
                        {selectedCheck.voided_at && (
                          <p className="text-xs text-red-500 mt-1">Voided {new Date(selectedCheck.voided_at).toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {selectedCheck.status === 'printed' && selectedCheck.print_snapshot && (
                  <div className="mt-3 flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => startReprint(selectedCheck)} data-testid="register-view-reprint-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reprint (Original Snapshot)
                    </Button>
                    <span className="text-xs text-slate-400">Uses the LLC data from when this check was first printed</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Void Reason Dialog */}
      <Dialog open={voidDialogOpen} onOpenChange={(open) => { if (!open) { setVoidDialogOpen(false); setVoidTargetCheck(null); setVoidReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="void-reason-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Void Check #{voidTargetCheck?.check_number}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reason for voiding <span className="text-slate-400">(optional but recommended)</span>
            </label>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g., Wrong amount, duplicate payment, payee changed..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 resize-none"
              rows={3}
              data-testid="void-reason-input"
              autoFocus
            />
            {voidTargetCheck && (
              <div className="mt-3 text-xs text-slate-500">
                <span className="font-medium">{voidTargetCheck.payee}</span> &middot; {formatCurrency(voidTargetCheck.amount)} &middot; {new Date(voidTargetCheck.date).toLocaleDateString()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidDialogOpen(false); setVoidTargetCheck(null); setVoidReason(''); }} data-testid="void-cancel-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmVoid} data-testid="void-confirm-btn">
              Void Check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden reprint container — uses print_snapshot LLC data */}
      {reprintCheck && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <div ref={reprintRef}>
            <CheckPrintView
              llc={reprintCheck.print_snapshot || getLLC(reprintCheck.llc_id)}
              check={reprintCheck}
              isPreview={false}
              isPreprinted={reprintCheck.is_preprinted || false}
              layout={checkLayout}
            />
          </div>
        </div>
      )}
    </div>
  );
};
